import { useEffect, useState } from 'react';
import { FileText, Printer, CheckSquare, Shield, AlertCircle, Check, X, ChevronDown, ChevronUp, Download } from 'lucide-react';
import { useAdminAuth } from '../AuthContext';
import { adminGeneratePacket, adminGetPacket, adminUpdateChecklistItem, adminUpdatePhysicalFiling, downloadDocumentPacketPdf, downloadPackingSlipPdf } from '../../services/api';

const STATUS_CFG = {
  not_started: { label: 'Pending',     cls: 'text-zinc-500 bg-zinc-800 border-zinc-700',    dot: 'bg-zinc-600' },
  generated:   { label: 'Generated',   cls: 'text-blue-400 bg-blue-500/10 border-blue-500/20', dot: 'bg-blue-500' },
  printed:     { label: 'Printed',     cls: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/20', dot: 'bg-yellow-500' },
  signed:      { label: 'Signed',      cls: 'text-orange-400 bg-orange-500/10 border-orange-500/20', dot: 'bg-orange-500' },
  filed:       { label: 'Filed',       cls: 'text-green-400 bg-green-500/10 border-green-500/20', dot: 'bg-green-500' },
  not_applicable: { label: 'N/A',      cls: 'text-zinc-600 bg-zinc-900 border-zinc-800',    dot: 'bg-zinc-700' },
};

const NEXT_STATUS = { not_started: 'printed', generated: 'printed', printed: 'signed', signed: 'filed', filed: 'filed' };

function ChecklistItem({ item, onUpdate, saving }) {
  const cfg = STATUS_CFG[item.status] || STATUS_CFG.not_started;
  const nextStatus = NEXT_STATUS[item.status];
  const [notes, setNotes] = useState(item.notes || '');

  return (
    <div data-testid={`checklist-item-${item.item_id}`} className={`border-b border-white/5 last:border-0 px-5 py-4 ${item.status === 'filed' ? 'bg-green-500/3' : ''}`}>
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${cfg.dot}`} />
          <div className="flex-1 min-w-0">
            <p className="font-inter text-sm text-white truncate">{item.description}</p>
            <div className="flex items-center gap-3 mt-0.5">
              <span className={`font-inter text-[10px] font-bold px-2 py-0.5 border uppercase tracking-wider ${cfg.cls}`}>{cfg.label}</span>
              {item.required && <span className="font-inter text-[10px] text-zinc-600">Required</span>}
              {!item.required && <span className="font-inter text-[10px] text-zinc-700">Optional</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {item.status !== 'filed' && item.status !== 'not_applicable' && (
            <button
              data-testid={`advance-${item.item_id}`}
              onClick={() => onUpdate(item.item_id, nextStatus, notes)}
              disabled={!!saving}
              className="flex items-center gap-1.5 font-inter text-xs font-medium border border-white/20 hover:border-orange-500/40 hover:bg-orange-500/10 hover:text-orange-400 text-zinc-400 px-3 py-1.5 transition-colors disabled:opacity-40"
            >
              {saving === item.item_id ? '...' : `→ ${nextStatus?.charAt(0).toUpperCase() + nextStatus?.slice(1)}`}
            </button>
          )}
          {item.status !== 'not_applicable' && (
            <button
              onClick={() => onUpdate(item.item_id, 'not_applicable', notes)}
              disabled={!!saving || item.status === 'not_applicable'}
              className="font-inter text-[10px] text-zinc-600 hover:text-zinc-400 transition-colors disabled:opacity-30"
              title="Mark N/A"
            >N/A</button>
          )}
        </div>
      </div>
      {/* Timestamps */}
      <div className="flex flex-wrap gap-4 mt-1.5 pl-5">
        {item.printed_at && <span className="font-inter text-[10px] text-zinc-600">Printed: {new Date(item.printed_at).toLocaleDateString()}</span>}
        {item.signed_at && <span className="font-inter text-[10px] text-zinc-600">Signed: {new Date(item.signed_at).toLocaleDateString()}</span>}
        {item.filed_at && <span className="font-inter text-[10px] text-zinc-600">Filed: {new Date(item.filed_at).toLocaleDateString()}</span>}
        {item.actor && <span className="font-inter text-[10px] text-zinc-700">by {item.actor}</span>}
      </div>
    </div>
  );
}

function PacketSection({ title, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="bg-[#1a1a1a] border border-white/10 mb-3">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-5 py-3 hover:bg-white/3 transition-colors">
        <span className="font-bebas text-base text-white tracking-wider">{title}</span>
        {open ? <ChevronUp size={14} className="text-zinc-400" /> : <ChevronDown size={14} className="text-zinc-400" />}
      </button>
      {open && <div className="border-t border-white/10">{children}</div>}
    </div>
  );
}

export default function AdminDocumentPacket({ orderId, orderNumber }) {
  const { token, user } = useAdminAuth();
  const [packet, setPacket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [saving, setSaving] = useState('');
  const [error, setError] = useState('');
  const [pdfDownloading, setPdfDownloading] = useState('');
  const [physicalForm, setPhysicalForm] = useState({ physical_file_location: '' });
  const [physicalSaving, setPhysicalSaving] = useState(false);

  const load = () => {
    adminGetPacket(token, orderId)
      .then(p => { setPacket(p); setPhysicalForm({ physical_file_location: p.physical_file_location || '' }); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [orderId, token]);

  const handleGenerate = async () => {
    setGenerating(true);
    setError('');
    try {
      const p = await adminGeneratePacket(token, orderId);
      setPacket(p);
      setPhysicalForm({ physical_file_location: p.physical_file_location || '' });
    } catch (err) { setError(err.message); }
    setGenerating(false);
  };

  const handleChecklistUpdate = async (itemId, status, notes) => {
    setSaving(itemId);
    try {
      const p = await adminUpdateChecklistItem(token, orderId, itemId, status, notes);
      setPacket(p);
    } catch (err) { setError(err.message); }
    setSaving('');
  };

  const handlePhysicalUpdate = async (field, value) => {
    setPhysicalSaving(true);
    try {
      const updates = { [field]: value };
      if (field === 'physically_printed_at' && !packet?.physically_printed_at) updates[field] = new Date().toISOString();
      if (field === 'physically_signed_at' && !packet?.physically_signed_at) updates[field] = new Date().toISOString();
      if (field === 'physically_filed_at' && !packet?.physically_filed_at) updates[field] = new Date().toISOString();
      if (field === 'physical_file_location') updates[field] = physicalForm.physical_file_location;
      const p = await adminUpdatePhysicalFiling(token, orderId, updates);
      setPacket(p);
    } catch (err) { setError(err.message); }
    setPhysicalSaving(false);
  };

  if (loading) {
    return <div className="flex items-center justify-center py-8"><div className="w-6 h-6 border-2 border-orange-500 border-t-transparent rounded-full animate-spin" /></div>;
  }

  const data = packet?.packet_data || {};
  const checklist = packet?.checklist || [];
  const allFiled = checklist.every(i => ['filed', 'not_applicable'].includes(i.status));
  const pendingRequired = checklist.filter(i => i.required && !['filed', 'signed'].includes(i.status)).length;

  return (
    <div data-testid="document-packet-panel" className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-bebas text-2xl text-white tracking-wider flex items-center gap-3">
            DOCUMENT PACKET
            {packet && (
              <span className={`font-inter text-[10px] font-bold px-2 py-0.5 border uppercase tracking-wider ${allFiled ? 'text-green-400 bg-green-500/10 border-green-500/20' : 'text-orange-400 bg-orange-500/10 border-orange-500/20'}`}>
                {packet.status}
              </span>
            )}
          </h3>
          {packet && (
            <p className="font-inter text-xs text-zinc-500 mt-0.5">
              {packet.packet_id} · v{packet.version} · Generated {new Date(packet.generated_at).toLocaleString()} by {packet.generated_by}
              {pendingRequired > 0 && <span className="ml-3 text-orange-400">{pendingRequired} required item{pendingRequired !== 1 ? 's' : ''} pending</span>}
            </p>
          )}
        </div>
        <button
          data-testid="generate-packet-button"
          onClick={handleGenerate}
          disabled={generating}
          className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-inter text-sm font-medium px-4 py-2.5 transition-colors"
        >
          <FileText size={14} />
          {generating ? 'Generating...' : packet ? 'Regenerate Packet' : 'Generate Packet'}
        </button>
        {packet && (
          <div className="flex gap-1">
            <button
              data-testid="download-packet-pdf-branded"
              onClick={async () => {
                setPdfDownloading('branded');
                try { await downloadDocumentPacketPdf(token, orderId, orderNumber, 'branded'); }
                catch (e) { setError(e.message); }
                setPdfDownloading('');
              }}
              disabled={!!pdfDownloading}
              className="flex items-center gap-1.5 font-inter text-xs bg-zinc-700 hover:bg-zinc-600 disabled:opacity-50 text-white px-3 py-2.5 transition-colors"
              title="Download branded PDF"
            >
              <Download size={13} />{pdfDownloading === 'branded' ? '...' : 'PDF'}
            </button>
            <button
              data-testid="download-packet-pdf-plain"
              onClick={async () => {
                setPdfDownloading('plain');
                try { await downloadDocumentPacketPdf(token, orderId, orderNumber, 'plain'); }
                catch (e) { setError(e.message); }
                setPdfDownloading('');
              }}
              disabled={!!pdfDownloading}
              className="flex items-center gap-1.5 font-inter text-xs bg-zinc-700 hover:bg-zinc-600 disabled:opacity-50 text-white px-3 py-2.5 transition-colors"
              title="Download physical (B&W) PDF"
            >
              <Download size={13} />{pdfDownloading === 'plain' ? '...' : 'B&W'}
            </button>
            <button
              data-testid="download-packing-slip-pdf"
              onClick={async () => {
                setPdfDownloading('slip');
                try { await downloadPackingSlipPdf(token, orderId, orderNumber, 'plain'); }
                catch (e) { setError(e.message); }
                setPdfDownloading('');
              }}
              disabled={!!pdfDownloading}
              className="flex items-center gap-1.5 font-inter text-xs bg-zinc-700 hover:bg-zinc-600 disabled:opacity-50 text-white px-3 py-2.5 transition-colors"
              title="Download packing slip PDF"
            >
              <Download size={13} />{pdfDownloading === 'slip' ? '...' : 'Slip'}
            </button>
          </div>
        )}
      </div>

      {error && <div className="bg-red-500/10 border border-red-500/30 text-red-400 font-inter text-sm px-4 py-3">{error}</div>}

      {!packet ? (
        <div className="text-center py-12 bg-[#1a1a1a] border border-white/10">
          <FileText size={36} className="text-zinc-700 mx-auto mb-3" />
          <p className="font-inter text-sm text-zinc-500">No packet generated yet. Click "Generate Packet" to create the document package.</p>
        </div>
      ) : (
        <>
          {/* Document Checklist */}
          <div className="bg-[#1a1a1a] border border-white/10">
            <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
              <h3 className="font-bebas text-lg text-white tracking-wider">DOCUMENT CHECKLIST</h3>
              <span className="font-inter text-xs text-zinc-500">
                {checklist.filter(i => ['signed','filed'].includes(i.status)).length}/{checklist.length} complete
              </span>
            </div>
            <div data-testid="document-checklist">
              {checklist.map(item => (
                <ChecklistItem key={item.item_id} item={item} onUpdate={handleChecklistUpdate} saving={saving} />
              ))}
            </div>
          </div>

          {/* Physical Document Mode */}
          <div className="bg-[#1a1a1a] border border-orange-500/20">
            <div className="px-5 py-4 border-b border-white/10 flex items-center gap-2">
              <Printer size={15} className="text-orange-500" />
              <h3 className="font-bebas text-lg text-white tracking-wider">PHYSICAL DOCUMENT MODE</h3>
              <span className="font-inter text-[10px] text-orange-400 border border-orange-500/20 bg-orange-500/10 px-2 py-0.5 ml-1">Print & File Workflow</span>
            </div>
            <div className="p-5 space-y-4">
              {/* Status row */}
              <div className="grid grid-cols-3 gap-3">
                {[
                  { key: 'physically_printed_at', label: 'Printed', icon: Printer, action: 'Mark Printed' },
                  { key: 'physically_signed_at', label: 'Signed', icon: CheckSquare, action: 'Mark Signed' },
                  { key: 'physically_filed_at', label: 'Filed', icon: Check, action: 'Mark Filed' },
                ].map(s => {
                  const done = !!packet[s.key];
                  const Icon = s.icon;
                  return (
                    <div key={s.key} className={`border p-3 text-center transition-all ${done ? 'border-green-500/30 bg-green-500/5' : 'border-white/10'}`}>
                      <Icon size={16} className={`mx-auto mb-1.5 ${done ? 'text-green-400' : 'text-zinc-600'}`} />
                      <div className="font-inter text-xs font-medium text-white">{s.label}</div>
                      {done ? (
                        <div className="font-inter text-[10px] text-green-400 mt-0.5">{new Date(packet[s.key]).toLocaleDateString()}</div>
                      ) : (
                        <button
                          data-testid={`mark-${s.key.replace('physically_', '').replace('_at', '')}`}
                          onClick={() => handlePhysicalUpdate(s.key, new Date().toISOString())}
                          disabled={physicalSaving}
                          className="font-inter text-[10px] text-orange-500 hover:text-white transition-colors mt-0.5 disabled:opacity-40"
                        >
                          {s.action}
                        </button>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Physical location */}
              {packet.physically_filed_at && (
                <div>
                  <label className="block font-inter text-xs text-zinc-500 uppercase tracking-wider mb-1.5">Physical File Location</label>
                  <div className="flex gap-2">
                    <input
                      data-testid="physical-file-location"
                      value={physicalForm.physical_file_location}
                      onChange={e => setPhysicalForm({ physical_file_location: e.target.value })}
                      className="flex-1 bg-[#1e1e1e] border border-white/10 focus:border-orange-500 text-white font-inter text-sm px-3 py-2 outline-none transition-colors placeholder-zinc-600"
                      placeholder="e.g. Cabinet A, Drawer 3, Folder 2026-NFA"
                    />
                    <button
                      data-testid="save-file-location"
                      onClick={() => handlePhysicalUpdate('physical_file_location', physicalForm.physical_file_location)}
                      disabled={physicalSaving}
                      className="font-inter text-sm text-zinc-400 hover:text-white border border-white/10 hover:border-white/30 px-3 py-2 transition-colors disabled:opacity-40"
                    >
                      Save
                    </button>
                  </div>
                  {packet.physical_file_location && (
                    <p className="font-inter text-xs text-green-400 mt-1">Saved: {packet.physical_file_location}</p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Document Previews */}
          <PacketSection title="ORDER SUMMARY" defaultOpen={true}>
            {data.internal_order_summary && (
              <div className="p-5 grid grid-cols-2 sm:grid-cols-3 gap-4">
                {[
                  { label: 'Order #', value: data.internal_order_summary.order_number },
                  { label: 'Type', value: data.internal_order_summary.order_type },
                  { label: 'Status', value: data.internal_order_summary.status },
                  { label: 'Payment', value: data.internal_order_summary.payment_status },
                  { label: 'Business', value: data.internal_order_summary.business_name },
                  { label: 'Total', value: `$${data.internal_order_summary.subtotal?.toFixed(2)}` },
                ].map(f => (
                  <div key={f.label}>
                    <p className="font-inter text-[10px] text-zinc-500 uppercase tracking-wider">{f.label}</p>
                    <p className="font-inter text-sm text-white mt-0.5">{f.value || '—'}</p>
                  </div>
                ))}
              </div>
            )}
          </PacketSection>

          <PacketSection title={`SERIALIZED ITEMS (${data.serialized_item_sheet?.items?.length || 0})`}>
            {data.serialized_item_sheet?.items?.length > 0 ? (
              <div className="divide-y divide-white/5">
                {data.serialized_item_sheet.items.map(s => (
                  <div key={s.serial_number} className="flex items-center justify-between px-5 py-3">
                    <div className="flex items-center gap-3">
                      <span className="font-mono text-sm text-orange-300">{s.serial_number}</span>
                      {s.nfa_item && <Shield size={12} className="text-orange-400" />}
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="font-inter text-xs text-zinc-500">{s.product_name}</span>
                      <span className={`font-inter text-[10px] font-bold px-2 py-0.5 border uppercase tracking-wider ${s.status === 'shipped' ? 'text-teal-400 bg-teal-500/10 border-teal-500/20' : 'text-zinc-400 bg-zinc-800 border-zinc-700'}`}>{s.status}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="p-5 font-inter text-sm text-zinc-600">No serial numbers assigned yet</p>
            )}
          </PacketSection>

          <PacketSection title="COMPLIANCE CHECKLIST">
            {data.compliance_checklist?.checks && (
              <div className="divide-y divide-white/5">
                {data.compliance_checklist.checks.map((c, i) => (
                  <div key={i} className="flex items-center justify-between px-5 py-3">
                    <span className="font-inter text-sm text-zinc-300">{c.label}</span>
                    <span className={`font-inter text-xs font-semibold flex items-center gap-1 ${
                      c.status === 'passed' ? 'text-green-400' :
                      c.status === 'failed' ? 'text-red-400' :
                      c.status === 'warning' ? 'text-yellow-400' : 'text-zinc-500'
                    }`}>
                      {c.status === 'passed' ? <Check size={12} /> : c.status === 'failed' ? <X size={12} /> : <AlertCircle size={12} />}
                      {c.status.toUpperCase()}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </PacketSection>

          <PacketSection title="SIGNATURE PAGE">
            {data.signature_page?.signatories && (
              <div className="p-5 space-y-4">
                {data.signature_page.signatories.map((sig, i) => (
                  <div key={i} className="border border-white/10 p-4">
                    <p className="font-inter text-xs text-zinc-500 uppercase tracking-wider mb-3">{sig.role}</p>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="font-inter text-[10px] text-zinc-600 mb-1">Printed Name</p>
                        <div className="border-b border-white/20 pb-2 min-h-[28px] font-inter text-sm text-zinc-700 italic">
                          {sig.name || '________________________________'}
                        </div>
                      </div>
                      <div>
                        <p className="font-inter text-[10px] text-zinc-600 mb-1">Date</p>
                        <div className="border-b border-white/20 pb-2 min-h-[28px] font-inter text-sm text-zinc-700 italic">
                          {sig.date || '________________'}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </PacketSection>
        </>
      )}
    </div>
  );
}
